// RadarPlotFilters.js
import { useEffect, useState, useCallback, useRef, useMemo } from "react";
import { AsyncPaginate } from "react-select-async-paginate";
import RadarPlot from "../components/RadarPlot";
import {
  saveToState,
  loadFromState,
  resetState,
  getPaginatedData,
} from "../utils";
import { getProfiles, getNodes, getRiskNodeSets } from "../api/quontxApi";

const ITEMS_PER_PAGE = 25;

// Domain name filters for each usecase
const USECASE_DOMAIN_FILTERS = {
  Source: "Source",
  Manifestation: "Manifestation",
  Governance: "Governance",
};

const RadarPlotFilters = ({ propagnateData }) => {
  const maxEntityCount = 5;

  const propagnateFunctionRef = useRef(propagnateData);

  // data keyed by SELECTED NODE UUID (entity uuid)
  const [data, setData] = useState({});
  const [loadingData, setLoadingData] = useState(false);

  const [activeDomains, setActiveDomains] = useState([]);
  const [activeEntities, setActiveEntities] = useState([]);
  const [activeUsecase, setActiveUsecase] = useState("Source");

  const [entitySelectDisabled, setEntitySelectDisabled] = useState(false);
  const [loadingDomains, setLoadingDomains] = useState(false);
  const [allDomains, setAllDomains] = useState([]);

  const hasAnySelected = (activeEntities?.length ?? 0) > 0;

  const toOption = (node, disabled) => ({
    label: node.name,
    value: node.uuid,
    isDisabled: disabled,
  });

  // ---------------------------
  // Load all domains on mount
  // ---------------------------

  const loadAllDomains = useCallback(async () => {
    try {
      const nodeSets = await getPaginatedData(getRiskNodeSets, { itemsPerPage: 100 });

      const sortedDomains = (nodeSets.data ?? []).slice().sort((a, b) => {
        if (a.description > b.description) return 1;
        if (a.description < b.description) return -1;
        return 0;
      });

      setAllDomains(sortedDomains);
    } catch (error) {
      console.error("[RadarPlotFilters] Error loading all domains:", error);
      setAllDomains([]);
    }
  }, []);

  useEffect(() => {
    loadAllDomains();
  }, [loadAllDomains]);

  // ---------------------------
  // Selection management
  // ---------------------------

  const manageActiveEntities = useCallback(
    (selectedEntities) => {
      const safe = selectedEntities ?? [];
      if (safe.length <= maxEntityCount) {
        setActiveEntities(safe.map((e) => ({ label: e.label, value: e.value })));
      }
    },
    [maxEntityCount]
  );

  const resetFilters = useCallback(() => {
    resetState("radarPlotState", [
      { stateFunc: setActiveEntities, nullVal: [] },
      { stateFunc: setActiveUsecase, nullVal: "Source" },
    ]);

    setActiveDomains([]);
    setData({});
    setEntitySelectDisabled(false);
  }, []);

  // ---------------------------
  // Server-side node search
  // ---------------------------

  const fetchNodesPage = useCallback(async ({ search, page, node_type }) => {
    const res = await getNodes({
      node_type,
      name: search,
      page,
      itemsPerPage: ITEMS_PER_PAGE,
    });
    return res?.data?.data ?? [];
  }, []);

  const loadEntityOptions = useCallback(
    async (search, _loadedOptions, additional) => {
      const q = (search || "").trim();
      if (q.length < 2) {
        return { options: [], hasMore: false, additional: { page: 1 } };
      }

      const page = additional?.page ?? 1;
      const items = await fetchNodesPage({
        search: q,
        page,
        node_type: "NAMED_ENTITY:ORGANISATION",
      });

      return {
        options: items.map((n) => toOption(n, entitySelectDisabled)),
        hasMore: items.length === ITEMS_PER_PAGE,
        additional: { page: page + 1 },
      };
    },
    [fetchNodesPage, entitySelectDisabled]
  );

  const entityAdditional = useMemo(() => ({ page: 1 }), []);

  // ---------------------------
  // Load domains for selected usecase
  // ---------------------------

  const loadDomainsForUsecase = useCallback(
    (usecase) => {
      setLoadingDomains(true);
      try {
        const domainFilter = USECASE_DOMAIN_FILTERS[usecase];

        // Filter domains by name containing the filter string
        const filteredDomains = allDomains.filter((domain) =>
          (domain.name || domain.description || "")
            .toLowerCase()
            .includes(domainFilter.toLowerCase())
        );

        setActiveDomains(
          filteredDomains.map((domain) => ({
            label: domain.description || domain.name,
            value: domain.uuid,
          }))
        );
      } catch (error) {
        console.error("[RadarPlotFilters] Error filtering domains:", error);
        setActiveDomains([]);
      } finally {
        setLoadingDomains(false);
      }
    },
    [allDomains]
  );

  // Load domains when usecase changes or allDomains are loaded
  useEffect(() => {
    if (allDomains.length > 0) {
      loadDomainsForUsecase(activeUsecase);
    }
  }, [activeUsecase, allDomains, loadDomainsForUsecase]);

  // ---------------------------
  // Load profiles
  // ---------------------------

  const loadProfiles = useCallback(async () => {
    const entityIds = activeEntities.map((e) => e.value);

    if (entityIds.length === 0) {
      setData({});
      return;
    }

    const profiles = await getProfiles({
      itemsPerPage: entityIds.length,
      entities: entityIds,
    });

    propagnateFunctionRef.current(profiles.data);

    const rows = profiles?.data?.data ?? [];

    // index returned profiles by uuid
    const profileByUuid = {};
    for (const r of rows) {
      const uuid = r?.metadata?.corpora?.uuid;
      if (uuid) profileByUuid[uuid] = r.values;
    }

    // build final data keyed by SELECTED UUIDs
    const next = {};

    // entities: use own uuid (must exist in response under their uuid)
    for (const id of entityIds) {
      if (profileByUuid[id]) next[id] = profileByUuid[id];
      else {
        console.warn("[RadarPlotFilters] Missing entity profile", {
          id,
          availableKeys: Object.keys(profileByUuid),
        });
      }
    }

    setData(next);
  }, [activeEntities]);

  // Enable/disable entity select based on maxEntityCount
  useEffect(() => {
    const totalSelected = activeEntities?.length ?? 0;
    if (totalSelected < maxEntityCount) {
      setEntitySelectDisabled(false);
    } else {
      setEntitySelectDisabled(true);
    }
  }, [activeEntities, maxEntityCount]);

  // ---------------------------
  // Restore/persist localStorage
  // ---------------------------

  useEffect(() => {
    loadFromState("radarPlotState", [
      {
        stateFunc: setActiveEntities,
        storageObjectKey: "activeEntities",
        defaultVal: [],
      },
      {
        stateFunc: setActiveUsecase,
        storageObjectKey: "activeUsecase",
        defaultVal: "Source",
      },
    ]);

    const fn = propagnateFunctionRef.current;
    return () => fn({});
  }, []);

  useEffect(() => {
    if (activeEntities.length > 0 || activeUsecase !== "Source") {
      saveToState("radarPlotState", {
        activeEntities,
        activeUsecase,
      });
    }
  }, [activeEntities, activeUsecase]);

  // ---------------------------
  // Trigger profile fetch
  // ---------------------------

  useEffect(() => {
    const totalSelected = activeEntities?.length ?? 0;

    if (totalSelected === 0) {
      setData({});
      return;
    }

    (async () => {
      setLoadingData(true);
      try {
        await loadProfiles();
      } finally {
        setLoadingData(false);
      }
    })();
  }, [activeEntities, loadProfiles]);

  return (
    <div className="w-full flex flex-col items-center content-center">
      {loadingDomains && <p>Loading domains...</p>}

      <div>
        <div className="flex flex-col gap-4 items-center justify-center mb-8">
          <div className="flex gap-4 justify-center mb-4">
            <div className="w-[240px] flex flex-col gap-2">
              <p>Organization Profiles</p>
              <AsyncPaginate
                isMulti
                value={activeEntities}
                onChange={manageActiveEntities}
                loadOptions={loadEntityOptions}
                additional={entityAdditional}
                debounceTimeout={250}
                cacheOptions
                placeholder="Type to search…"
              />
            </div>
          </div>
          <button
            onClick={resetFilters}
            className="bg-blue-500 text-white font-semibold py-2 px-4 rounded hover:bg-blue-600 transition duration-200"
          >
            Reset filters
          </button>
          <div className="flex gap-4 justify-center mb-4">
            <button
              onClick={() => setActiveUsecase("Source")}
              className={`font-semibold py-2 px-4 rounded transition duration-200 ${
                activeUsecase === "Source"
                  ? "bg-blue-500 text-white hover:bg-blue-600"
                  : "bg-gray-200 text-gray-700 hover:bg-gray-300"
              }`}
            >
              Source
            </button>
            <button
              onClick={() => setActiveUsecase("Manifestation")}
              className={`font-semibold py-2 px-4 rounded transition duration-200 ${
                activeUsecase === "Manifestation"
                  ? "bg-blue-500 text-white hover:bg-blue-600"
                  : "bg-gray-200 text-gray-700 hover:bg-gray-300"
              }`}
            >
              Manifestation
            </button>
            <button
              onClick={() => setActiveUsecase("Governance")}
              className={`font-semibold py-2 px-4 rounded transition duration-200 ${
                activeUsecase === "Governance"
                  ? "bg-blue-500 text-white hover:bg-blue-600"
                  : "bg-gray-200 text-gray-700 hover:bg-gray-300"
              }`}
            >
              Governance
            </button>
          </div>
        </div>

        {activeDomains?.length >= 3 &&
          hasAnySelected &&
          Object.keys(data ?? {}).length > 0 && (
            <div className="flex items-center content-center flex-wrap">
              <RadarPlot
                isLoading={loadingData}
                data={data}
                timebucketIdx={0}
                activeDomains={activeDomains}
                activeEntities={activeEntities}
                activeProxies={[]}
              />
            </div>
          )}
      </div>
    </div>
  );
};

export default RadarPlotFilters;